import {Injectable, signal} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {Tile, TILE_TYPE_SIMPLE_ANALOG, TileId, TileType} from '../../shared/models/tile';
import {HistoryInterval, TileDataset, TileDatasetMap} from '../../shared/models/tile-dataset';
import {PointValueQuery} from '../../shared/models/point';
import {DevicesService} from './devices.service';
import {ShadeToolsService} from './shade-tools.service';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';
import {TUNNEL_BASE_URL} from './env.service';
import {DeviceType} from '../../shared/models/device';
import {SimpleStructure} from '../../shared/models/structure';
import {TimeRange, TimeRangeMode} from '../../shared/models/time-range';
import {
  HISTORY_QUANTITY,
  INTERVAL_OPTIONS_ANALOG,
  INTERVAL_OPTIONS_ANALOG_DEFAULT,
  TWO_MONTHS_MS,
  TWO_WEEKS_MS,
} from '../../shared/constants/tile-history.constants';
import {NavigationDirection, TileHistoryNavigationHelper} from './tile-history-navigation.helper';

@Injectable({
  providedIn: 'root',
})
export class TileHistoryService {
  drawerDatasets$: BehaviorSubject<{ [tileId: TileId]: TileDataset }> = new BehaviorSubject<{ [tileId: TileId]: TileDataset }>({});
  drawerInterval$: BehaviorSubject<HistoryInterval> = new BehaviorSubject<HistoryInterval>(null);
  /**
   * Whether the chart will add to (instead of replace) the current chart
   * the next time a user clicks "view history" for a tile, such that multiple
   * tiles' data are appearing simultaneously
   */
  drawerStacking$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /**
   * Literally just whether the chart should appear on the screen
   */
  drawerActive$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /**
   * Set to true whenever the chart is changing data sources, intervals, or other
   * actions that are not simply loading the latest data after having loaded once
   * already
   */
  drawerLoading = signal<boolean>(false);

  /**
   * Time range mode: 'latest' or 'custom'
   */
  drawerTimeRangeMode = signal<TimeRangeMode>(TimeRangeMode.Latest);

  /**
   * The navigation range being fetched
   * Used for API calls when navigating older/newer and for custom date range selection
   */
  navigationRange: TimeRange = {start: null, end: null};

  /**
   * Tracks whether the current range originated from explicit user date selection via date picker.
   * When true: user picked specific dates, so don't auto-switch to Latest when reaching present time.
   * When false: range came from navigation (Latest → Older/Newer), so auto-switch to Latest when appropriate.
   */
  private isUserSelectedRange: boolean = false;

  /**
   * The original range explicitly selected by the user via date picker
   * Used to determine if navigation moves outside the user's intended viewing range
   * Exposed as signal for reactive updates in components
   */
  userSelectedRange = signal<TimeRange>({start: null, end: null});

  drawerTimezone = signal<string>(Intl.DateTimeFormat().resolvedOptions().timeZone);

  private datasets: TileDatasetMap = {};
  private shownTiles: Tile[] = [];
  private interval: HistoryInterval;
  private ascending: boolean = false;
  private stacking: boolean = false;
  private cancelPreviousRequests$ = new Subject<void>();


  constructor(
    private deviceService: DevicesService,
    private shadeService: ShadeToolsService,
    private http: HttpClient,
  ) {
  }


  deactivate(): void {
    this.shownTiles = [];
    this.datasets = {};
    this.drawerDatasets$.next({});
    this.drawerActive$.next(false);

    // Reset to latest mode when closing the drawer
    this.drawerTimeRangeMode.set(TimeRangeMode.Latest);
    this.navigationRange = {start: null, end: null};
    this.isUserSelectedRange = false;
    this.userSelectedRange.set({start: null, end: null});
  }


  addToDrawerTiles(tiles: Tile[]): void {
    let newShownTiles: Tile[];

    if (this.stacking) {
      newShownTiles = [...this.shownTiles, ...tiles];

      if (newShownTiles.length > 1) {
        for (let tile of tiles) {
          if (!tile.typeSupportsStackedTileHistory()) {
            this.shadeService.simpleOkDialog(
              'Cannot Stack Tile',
              'This type of tile does not support stacking multiple tile histories on a single chart. To view history for this tile alone, first change the tile stacking setting from "Multiple" to "Single" and then try again.',
            );

            return;
          }
        }
      }
    } else {
      this.deactivate();
      newShownTiles = tiles;
    }

    this.isLoadingView = true;
    setTimeout(() => this.fetchDrawerHistory(newShownTiles), 0);
  }


  changeDrawerInterval(interval: HistoryInterval) {
    if (interval === this.interval) {
      return;
    }

    this.interval = interval;
    this.drawerInterval$.next(interval);
    this.fetchDrawerHistory(this.shownTiles, this.ascending);
    this.isLoadingView = true;
  }


  changeDrawerTimezone(timezone: string): void {
    this.drawerTimezone.set(timezone);
  }

  changeDrawerStacking(enabled: boolean) {
    this.stacking = enabled;
    this.drawerStacking$.next(enabled);
  }


  changeDrawerTimeRangeMode(mode: TimeRangeMode) {
    this.drawerTimeRangeMode.set(mode);

    if (mode === TimeRangeMode.Latest) {
      this.navigationRange = {start: null, end: null};
      this.isUserSelectedRange = false;
      this.ascending = false;
      this.userSelectedRange.set({start: null, end: null});
      this.fetchDrawerHistory(this.shownTiles);
      this.isLoadingView = true;
    }
  }


  changeDrawerTimeRange(range: TimeRange, ascending: boolean = false) {
    this.drawerTimeRangeMode.set(TimeRangeMode.Custom);
    this.navigationRange = range;
    this.isUserSelectedRange = true; // User explicitly selected this range via date picker
    this.ascending = ascending;
    this.userSelectedRange.set(range); // Store the original user-selected range
    this.fetchDrawerHistory(this.shownTiles, ascending);
    this.isLoadingView = true;
  }


  /**
   * Calculate seek length based on tile protocols (PLAT-242)
   * - 2 months for LoRaWAN tiles
   * - 2 weeks for BACnet and Modbus tiles
   * If tiles have mixed protocols, use the shortest duration (2 weeks)
   */
  getSeekLengthMs(tiles: Tile[]): number {
    // Check if any tile is BACnet or Modbus
    const hasNonLoRaWAN = tiles.some(tile => {
      const protocol = (tile.Structure as SimpleStructure)?.Protocol;
      return protocol === DeviceType.BACnet || protocol === DeviceType.Modbus;
    });

    // If any tile is BACnet or Modbus, use 2 weeks. Otherwise use 2 months (for LoRaWAN)
    return hasNonLoRaWAN ? TWO_WEEKS_MS : TWO_MONTHS_MS;
  }

  /**
   * Checks if timestamp is an ISO string (vs numeric milliseconds)
   */
  private isIsoString(timestamp: number | string): timestamp is string {
    return typeof timestamp === 'string';
  }

  /**
   * Navigate to older data (go back in time)
   * PLAT-248: Use Truncated flag to determine if adjustment is needed.
   * PLAT-277: Accepts ISO string to preserve microsecond precision.
   */
  navigateOlder(oldestTimestamp: number | string, wasTruncated: boolean = false) {
    const seekLengthMs = this.getSeekLengthMs(this.shownTiles);
    this.drawerTimeRangeMode.set(TimeRangeMode.Custom);

    // Use helper to calculate navigation range
    const range = TileHistoryNavigationHelper.calculateNavigationRange(
      oldestTimestamp,
      seekLengthMs,
      NavigationDirection.OLDER,
      wasTruncated
    );

    this.navigationRange = {
      start: range.start,
      end: range.end,
      startIso: typeof range.rangeStartForApi === 'string' ? range.rangeStartForApi : undefined,
      endIso: typeof range.rangeEndForApi === 'string' ? range.rangeEndForApi : undefined,
    };

    this.fetchDrawerHistoryWithRange(this.shownTiles, range.rangeStartForApi, range.rangeEndForApi, false, true);
    this.isLoadingView = true;
  }


  /**
   * Navigate to newer data (go forward in time)
   * PLAT-248: Use Truncated flag to determine if adjustment is needed.
   * PLAT-277: Accepts ISO string to preserve microsecond precision.
   */
  navigateNewer(newestTimestamp: number | string, wasTruncated: boolean = false) {
    const seekLengthMs = this.getSeekLengthMs(this.shownTiles);
    this.drawerTimeRangeMode.set(TimeRangeMode.Custom);

    // Use helper to calculate navigation range
    const range = TileHistoryNavigationHelper.calculateNavigationRange(
      newestTimestamp,
      seekLengthMs,
      NavigationDirection.NEWER,
      wasTruncated
    );

    this.navigationRange = {
      start: range.start,
      end: range.end,
      startIso: typeof range.rangeStartForApi === 'string' ? range.rangeStartForApi : undefined,
      endIso: typeof range.rangeEndForApi === 'string' ? range.rangeEndForApi : undefined,
    };

    this.fetchDrawerHistoryWithRange(this.shownTiles, range.rangeStartForApi, range.rangeEndForApi, true, true);
    this.isLoadingView = true;
  }

  private set isLoadingView(loading: boolean) {
    this.drawerLoading.set(loading);
  }


  /**
   * This is specifically for the tile history drawer.
   *
   * @param tiles - Tiles to fetch history for
   * @param ascending - Whether to fetch in ascending order (for navigateNewer)
   * @param checkRangeBounds - Whether to check if data moved outside user-selected range (only during navigation)
   */
  fetchDrawerHistory(tiles: Tile[], ascending: boolean = false, checkRangeBounds: boolean = false): void {
    const range = this.navigationRange;
    this.fetchDrawerHistoryWithRange(tiles, range.start, range.end, ascending, checkRangeBounds);
  }

  /**
   * Fetch drawer history with explicit range (supports both number and ISO string).
   * PLAT-277: Detects "reached latest" when RangeEnd >= now and Truncated = false.
   */
  private fetchDrawerHistoryWithRange(
    tiles: Tile[],
    rangeStart: number | string | null,
    rangeEnd: number | string | null,
    ascending: boolean = false,
    checkRangeBounds: boolean = false
  ): void {

    const validatedTiles = this.validateTiles(tiles);
    if (!validatedTiles.length) return;
    this.shownTiles = validatedTiles;

    this.validateAndUpdateInterval(this.shownTiles[0].Type);
    this.cancelPreviousRequests$.next();
    this.drawerActive$.next(true);

    let completedRequests = 0;
    const requestedRange = this.navigationRange;

    // Check if RangeEnd is in the future (>= now)
    const now = Date.now();
    const rangeEndMs = rangeEnd ? TileHistoryNavigationHelper.toMilliseconds(rangeEnd) : now;
    const isQueryingFuture = rangeEndMs >= now;

    for (let tile of this.shownTiles) {
      this.fetchHistory$(tile, HISTORY_QUANTITY, this.interval, rangeStart, rangeEnd, ascending)
        .pipe(takeUntil(this.cancelPreviousRequests$))
        .subscribe({
          next: (ds) => {
            this.datasets[tile.ID] = ds;

            if (++completedRequests === this.shownTiles.length) {
              this.handleFetchComplete(requestedRange, isQueryingFuture, ascending, checkRangeBounds);
            }
          },
          error: () => {
            completedRequests++;
            this.drawerDatasets$.next(this.datasets);
            this.isLoadingView = false;
          }
        });
    }
  }

  /**
   * Handles completion of all tile fetch requests.
   */
  private handleFetchComplete(
    requestedRange: TimeRange,
    isQueryingFuture: boolean,
    ascending: boolean,
    checkRangeBounds: boolean
  ): void {
    if (checkRangeBounds && this.isUserSelectedRange) {
      this.clearUserRangeIfDataOutsideBounds(requestedRange);
    }

    // PLAT-277: Check if we've reached latest data when navigating newer
    // If querying into future AND got non-truncated data, we've reached the end (latest)
    if (ascending && !this.isUserSelectedRange && isQueryingFuture) {
      const allNonTruncated = this.shownTiles.every(tile => {
        const dataset = this.datasets[tile.ID];
        return dataset && !dataset.truncated;
      });

      if (allNonTruncated) {
        this.drawerTimeRangeMode.set(TimeRangeMode.Latest);
        this.navigationRange = {start: null, end: null};
        this.isUserSelectedRange = false;
        this.ascending = false;
        this.userSelectedRange.set({start: null, end: null});
        this.fetchDrawerHistory(this.shownTiles);
        return;
      }
    }

    this.drawerDatasets$.next(this.datasets);
    this.isLoadingView = false;
  }

  /**
   * Clears user-selected range flag if ACTUAL DATA moved outside user's selected boundaries.
   * PLAT-277: Checks actual data timestamps, not search window.
   * Also clears if no data received AND search window is outside user's range.
   */
  private clearUserRangeIfDataOutsideBounds(requestedRange: TimeRange): void {
    const userRange = this.userSelectedRange();

    if (!userRange.start || !userRange.end) {
      return;
    }

    // Extract actual data boundaries from all datasets using helper
    const datasets = Object.values(this.datasets);
    const { oldest, newest } = TileHistoryNavigationHelper.extractBoundaries(datasets);

    // If no data received, check if SEARCH WINDOW moved outside user's range
    if (!oldest || !newest) {
      const searchOutsideBounds = requestedRange.start < userRange.start || requestedRange.end > userRange.end;
      if (searchOutsideBounds) {
        this.isUserSelectedRange = false;
        this.userSelectedRange.set({start: null, end: null});
      }
      return;
    }

    // Convert ISO strings to milliseconds for comparison
    const oldestMs = new Date(oldest).getTime();
    const newestMs = new Date(newest).getTime();

    // Check if ACTUAL DATA moved outside user's selected bounds
    const isOutsideBounds = oldestMs < userRange.start || newestMs > userRange.end;

    if (isOutsideBounds) {
      this.isUserSelectedRange = false;
      this.userSelectedRange.set({start: null, end: null});
    }
  }

  private validateTiles(tiles: Tile[]): Tile[] {
    const uniqueTiles = new Map<TileId, Tile>();
    let tileType: TileType = '';

    for (let tile of tiles) {
      if (!uniqueTiles.has(tile.ID)) {
        uniqueTiles.set(tile.ID, Tile.create(tile));
        if (!tileType) {
          tileType = tile.Type;
        } else if (tile.Type !== tileType) {
          throw new Error('cannot load tile history for more than one tile type simultaneously');
        }
      }
    }

    const validated = Array.from(uniqueTiles.values());

    if (validated.length > 5) {
      this.shadeService.simpleOkDialog('Reached Maximum', 'Only 5 data points can be displayed at one time');
      return [];
    }

    return validated;
  }

  private validateAndUpdateInterval(tileType: TileType): void {
    const isAnalog = tileType === TILE_TYPE_SIMPLE_ANALOG;

    // PLAT-243: Non-analog tiles (bool/multistate) only support 'none' interval
    if (!isAnalog && this.interval !== 'none') {
      this.interval = 'none';
      this.drawerInterval$.next('none');
    } else if (isAnalog && !INTERVAL_OPTIONS_ANALOG.some(o => o.id === this.interval)) {
      // Analog tile with invalid interval - reset to default
      this.interval = INTERVAL_OPTIONS_ANALOG_DEFAULT;
      this.drawerInterval$.next(INTERVAL_OPTIONS_ANALOG_DEFAULT);
    }
  }

  /**
   * A chart located anywhere, including the drawer, can use this function
   * to get tile history values from the API.
   *
   * @param tile
   * @param quantity
   * @param interval
   * @param rangeStart Optional start of time range (Unix timestamp in ms or ISO string)
   * @param rangeEnd Optional end of time range (Unix timestamp in ms or ISO string)
   * @param ascending Optional sort order (true = oldest first, false/null = newest first)
   */
  fetchHistory$(
    tile: Tile,
    quantity: number = 50,
    interval: HistoryInterval = 'none',
    rangeStart: number | string | null = null,
    rangeEnd: number | string | null = null,
    ascending: boolean | null = null
  ): Observable<TileDataset> {
    const params: any = {
      TileID: '' + tile.ID,
      Quantity: '' + quantity,
      Interval: interval,
    };

    // Add range parameters if specified (convert to RFC3339 format for API)
    // PLAT-277: If already ISO string, use directly to preserve precision
    if (rangeStart !== null) {
      params.RangeStart = this.isIsoString(rangeStart)
        ? rangeStart
        : new Date(rangeStart).toISOString();
    }
    if (rangeEnd !== null) {
      params.RangeEnd = this.isIsoString(rangeEnd)
        ? rangeEnd
        : new Date(rangeEnd).toISOString();
    }
    if (ascending !== null) {
      params.Ascending = ascending;
    }

    return this.http.get<PointValueQuery>(`${TUNNEL_BASE_URL}/remote/history/tile-values`, { params })
      .pipe(
        map((resp) => {
          // Extract oldest and newest timestamps from response data using helper
          const values = resp.TileValues || [];
          const { oldestTimestampIso, newestTimestampIso } = TileHistoryNavigationHelper.extractBoundariesFromValues(
            values,
            ascending
          );

          return {
            tile,
            data: resp.TileValues,
            interval,
            truncated: resp.Truncated,
            fetchedAscending: ascending,
            oldestTimestampIso,
            newestTimestampIso,
          };
        })
      );
  }


  /**
   * Fetches tile history as CSV from the API
   * @param tileId - The ID of the tile
   * @param interval - The aggregation interval
   * @param rangeStart - Start of time range (Unix timestamp in ms)
   * @param rangeEnd - End of time range (Unix timestamp in ms)
   * @returns Observable of HTTP response containing CSV string and headers
   */
  fetchHistoryCsv$(
    tileId: TileId,
    interval: HistoryInterval,
    rangeStart: number,
    rangeEnd: number
  ): Observable<HttpResponse<string>> {
    const params: any = {
      TileID: '' + tileId,
      Interval: interval,
      RangeStart: new Date(rangeStart).toISOString(),
      RangeEnd: new Date(rangeEnd).toISOString(),
    };

    return this.http.get(`${TUNNEL_BASE_URL}/remote/history/tile-values`, {
      params,
      headers: {
        'Accept': 'text/csv'
      },
      responseType: 'text',
      observe: 'response'
    });
  }
}
